import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { showToast } from "src/componentes";
import { AutenticacaoModule } from "src/Contexto/auth";
import {
  FormatarCPF,
  FormatarTelefone,
  MapearErrosServidor,
  MapearErrosServidorCampo,
  SoNumeros,
} from "src/hook/Functions";
import {
  UsuarioGestaoAtualizar,
  UsuarioGestaoBuscarPorId,
} from "src/services/Identity.Service";
import { EmpresaDto } from "../../Entidade/Empresa/Empresa.Dto";
import {
  MinhaContaDto,
  MinhaContaSchema,
} from "../../MinhaConta/MinhaConta.Dto";
import {
  getCadastroInicialLocal,
  limparCadastroInicialLocal,
  limparPendenteCadastroUsuario,
  setCadastroInicialLocal,
  STORAGE_CADASTRO_INICIAL_EMPRESA,
  STORAGE_CADASTRO_INICIAL_USUARIO,
} from "../CadastroInicial.Dto";

export const CadastroInicialUsuarioCode = () => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { userId, empresaId } = AutenticacaoModule();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    getValues,
  } = useForm<MinhaContaDto>({
    resolver: yupResolver(MinhaContaSchema),
    defaultValues: undefined,
  });

  useEffect(() => {
    // Valida se a etapa 1 foi preenchida ou se já tem empresa vinculada
    const empresaSalva = getCadastroInicialLocal<EmpresaDto>(
      STORAGE_CADASTRO_INICIAL_EMPRESA
    );
    if (!empresaSalva && Number(empresaId) === 0) {
      showToast({
        message: "Por favor, preencha os dados da empresa primeiro.",
        title: "Atenção",
        variant: "Warning",
      });
      navigate("/cadastro-inicial/empresa");
      return;
    }

    const controller = new AbortController();
    const Load = async () => {
      setLoading(true);
      await UsuarioGestaoBuscarPorId(userId.toString())
        .then((response) => {
          if (response.data?.success && response.data?.data) {
            const data = response.data.data as MinhaContaDto;
            data.documento = data.documento ? FormatarCPF(data.documento) : "";
            data.numeroTelefone = data.numeroTelefone
              ? FormatarTelefone(data.numeroTelefone)
              : "";
            if (data.dataNascimento) {
              data.dataNascimento = moment(data.dataNascimento).format(
                "YYYY-MM-DD"
              );
            }

            const savedUsuario = getCadastroInicialLocal<MinhaContaDto>(
              STORAGE_CADASTRO_INICIAL_USUARIO
            );

            if (savedUsuario) {
              const doc = savedUsuario.documento || data.documento || "";
              const tel = savedUsuario.numeroTelefone || data.numeroTelefone || "";
              reset({
                ...data,
                ...savedUsuario,
                nome: data.nome || savedUsuario.nome,
                sobrenome: data.sobrenome || savedUsuario.sobrenome,
                email: data.email || savedUsuario.email,
                documento: doc ? FormatarCPF(doc) : "",
                numeroTelefone: tel ? FormatarTelefone(tel) : "",
                dataNascimento:
                  savedUsuario.dataNascimento || data.dataNascimento,
              });
            } else {
              reset(data);
            }
          } else {
            console.log(response.data?.notifications);
            const savedUsuario = getCadastroInicialLocal<MinhaContaDto>(
              STORAGE_CADASTRO_INICIAL_USUARIO
            );
            if (savedUsuario) {
              savedUsuario.documento = savedUsuario.documento
                ? FormatarCPF(savedUsuario.documento)
                : "";
              savedUsuario.numeroTelefone = savedUsuario.numeroTelefone
                ? FormatarTelefone(savedUsuario.numeroTelefone)
                : "";
              reset(savedUsuario);
            }
          }
        })
        .catch((error) => {
          console.log(error);
          const savedUsuario = getCadastroInicialLocal<MinhaContaDto>(
            STORAGE_CADASTRO_INICIAL_USUARIO
          );
          if (savedUsuario) {
            savedUsuario.documento = savedUsuario.documento
              ? FormatarCPF(savedUsuario.documento)
              : "";
            savedUsuario.numeroTelefone = savedUsuario.numeroTelefone
              ? FormatarTelefone(savedUsuario.numeroTelefone)
              : "";
            reset(savedUsuario);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    };

    Load();
    return () => {
      controller.abort();
    };
  }, [userId, empresaId, reset, navigate]);

  const handleVoltar = () => {
    setCadastroInicialLocal(
      STORAGE_CADASTRO_INICIAL_USUARIO,
      getValues()
    );
    navigate("/cadastro-inicial/empresa");
  };

  const onSubmit = async (usuarioData: MinhaContaDto, e: any) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const usuarioPayload: MinhaContaDto = {
        ...usuarioData,
        id: userId,
        dataNascimento: moment(usuarioData.dataNascimento).format("YYYY-MM-DD"),
        documento: SoNumeros(usuarioData.documento),
        numeroTelefone: SoNumeros(usuarioData.numeroTelefone),
      };

      const resUsuario = await UsuarioGestaoAtualizar(usuarioPayload);

      if (!resUsuario.data?.success) {
        MapearErrosServidorCampo(resUsuario.data?.notifications, setError);
        MapearErrosServidor(resUsuario.data?.notifications, showToast);
        showToast({
          message:
            resUsuario.data?.notifications?.[0]?.message ||
            "Ocorreu um erro ao atualizar os dados do usuário.",
          title: "Erro ao atualizar usuário",
          variant: "Danger",
        });
        setSubmitting(false);
        return;
      }

      limparCadastroInicialLocal();
      limparPendenteCadastroUsuario();

      showToast({
        message: "Cadastro concluído com sucesso!",
        title: "Cadastro Concluído",
        variant: "Success",
      });

      navigate("/inicio");
    } catch (error) {
      console.error("Erro no processamento do cadastro:", error);
      showToast({
        message: "Ocorreu um erro ao processar o cadastro final.",
        title: "Erro",
        variant: "Danger",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return {
    loading,
    submitting,
    register,
    handleSubmit,
    errors,
    onSubmit,
    handleVoltar,
  };
};

export default CadastroInicialUsuarioCode;

